import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import { PDFViewer } from '@react-pdf/renderer';

import { getPresignedURL, loadPdfFromPresignedURL } from 'utils/ReportUtils';
import { REPORT_TEMPLATE_LIST } from 'components/Report/Templates/constants';
import logger from 'utils/logger';

const propTypes = {
  reportContent: PropTypes.object.isRequired,
  debugBox: PropTypes.object.isRequired,
  reRender: PropTypes.number.isRequired,
  onLoadingStateChange: PropTypes.func.isRequired,
};

const log = logger.createLogger('IssueDetailsReportsPreview');

const ReportPreview = ({
  reportContent,
  debugBox,
  reRender,
  onLoadingStateChange,
}) => {
  const reportTitle = reportContent.savedFileName + '.pdf';
  const [modifiedPdfUrl, setModifiedPdfUrl] = useState('');
  const [templateComponent, setTemplateComponent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const localReportList = REPORT_TEMPLATE_LIST();
  const debouncedUpdateRef = useRef();
  const latestValuesRef = useRef({
    reportContent,
    reportTitle,
    debugBox,
  });

  // Notify parent component about loading state changes
  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange(isLoading);
    }
  }, [isLoading, onLoadingStateChange]);

  useEffect(() => {
    debouncedUpdateRef.current = debounce(() => {
      const { reportContent, debugBox } = latestValuesRef.current;
      setIsLoading(true);
      if (reportContent.isLocal) {
        const TemplateComponent = localReportList.find(
          (t) => t.title === reportContent.title
        ).template;
        setTemplateComponent(
          <TemplateComponent
            reportTitle={reportTitle}
            reportContent={reportContent}
          />
        );
        setIsLoading(false);
        return;
      }

      getPresignedURL(reportContent.id, reportContent.s3FilePath)
        .then((presignedUrl) => {
          loadPdfFromPresignedURL(presignedUrl, reportContent, debugBox).then(
            (url) => {
              setModifiedPdfUrl(url);
            }
          );
        })
        .catch((error) => {
          log.error('error', error);
          setIsLoading(false);
        });
    }, 500);
  }, []);

  useEffect(() => {
    latestValuesRef.current = { reportContent, reportTitle, debugBox };
    if (debouncedUpdateRef.current) {
      debouncedUpdateRef.current();
    }
  }, [reportContent, reportTitle, reRender]);

  return (
    <>
      {reportContent.isLocal ? (
        <PDFViewer
          width="100%"
          height="100%"
          data-testid="reportPreviewModal-PDFViewer">
          {templateComponent}
        </PDFViewer>
      ) : (
        <iframe
          title={reportTitle}
          src={modifiedPdfUrl}
          width="100%"
          height="100%"
          style={{
            transform: 'scale(1)',
            transformOrigin: 'top left',
          }}
          onLoad={() => setIsLoading(false)}
        />
      )}
    </>
  );
};

ReportPreview.propTypes = propTypes;

export default ReportPreview;
